import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, ChevronLeft, ChevronRight, Images, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, assetUrl } from "../lib/api";

const fallbackImages = [
  { src: "/images/IMG-20250909-WA0012.jpg", caption: "Campus life" },
  { src: "/images/IMG-20250909-WA0013.jpg", caption: "Students on campus" },
  { src: "/images/IMG-20250909-WA0015.jpg", caption: "Institutional activity" },
  { src: "/images/IMG-20250909-WA0016.jpg", caption: "Academic community" },
  { src: "/images/Gal1.jpg", caption: "Campus view" },
  { src: "/images/Gal2.jpg", caption: "Lecture environment" },
  { src: "/images/Gal3.jpg", caption: "Library and learning" },
  { src: "/images/Gal4.jpg", caption: "Laboratory activity" },
];

function normalizeGalleryItem(item) {
  return {
    id: item.id,
    src: assetUrl(item.media_url || item.src),
    caption: item.caption || item.title || item.album_title || "Official campus media",
    title: item.title || item.caption || "Gallery media",
    category: item.category || item.album_title || "Campus",
  };
}

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.05 } }),
};

const Gallery = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(null);
  const [cmsImages, setCmsImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const maxPreview = 8;

  useEffect(() => {
    let mounted = true;
    api("/api/gallery/media?media_type=image")
      .then((rows) => {
        if (!mounted) return;
        const images = Array.isArray(rows) ? rows.filter((item) => item.media_url).map(normalizeGalleryItem) : [];
        setCmsImages(images);
      })
      .catch(() => mounted && setCmsImages([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const images = useMemo(() => (cmsImages.length ? cmsImages : fallbackImages.map(normalizeGalleryItem)), [cmsImages]);
  const visibleImages = images.slice(0, maxPreview);

  const openModal = (index) => setCurrentIndex(index);
  const closeModal = () => setCurrentIndex(null);
  const showPrev = () => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  const showNext = () => setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));

  useEffect(() => {
    const handleKey = (e) => {
      if (currentIndex === null) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [currentIndex, images.length]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50 py-24">
      <div className="absolute inset-0 opacity-70">
        <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-emerald-200/40 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="mx-auto mb-12 max-w-3xl text-center"
        >
          <p className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-blue-700">
            <Images className="h-4 w-4" /> Campus Media
          </p>
          <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950 md:text-6xl">Gallery</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-600">
            Recent official images uploaded from the gallery CMS, including campus life, events, facilities and institutional moments.
          </p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => <div key={index} className="aspect-square animate-pulse rounded-[1.5rem] bg-slate-200" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-6">
            {visibleImages.map((img, index) => (
              <motion.figure
                key={img.id || `${img.src}-${index}`}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -6, scale: 1.02 }}
                className={`group relative cursor-pointer overflow-hidden rounded-[1.35rem] bg-slate-100 shadow-lg shadow-slate-200/60 ring-1 ring-slate-200 ${index === 0 ? "sm:col-span-2 sm:row-span-2" : ""}`}
                onClick={() => openModal(index)}
              >
                <img
                  src={assetUrl(img.src)}
                  alt={img.caption}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = "/image-fallback.svg"; }}
                  className="h-full min-h-[180px] w-full aspect-square object-cover transition duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent opacity-0 transition duration-500 group-hover:opacity-100" />
                <figcaption className="absolute inset-x-0 bottom-0 translate-y-4 p-4 opacity-0 transition duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <span className="mb-2 inline-flex rounded-full bg-white/20 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-white backdrop-blur">{img.category}</span>
                  <p className="text-sm font-black text-white sm:text-base">{img.caption}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <motion.button
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-700 to-emerald-600 px-8 py-4 text-sm font-black text-white shadow-xl shadow-blue-700/20"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/gallery")}
          >
            View Full Gallery <Sparkles className="h-4 w-4" />
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {currentIndex !== null && images[currentIndex] && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <button onClick={closeModal} className="absolute right-4 top-4 z-10 rounded-full bg-black/45 p-3 text-white" aria-label="Close gallery preview"><X className="h-6 w-6" /></button>
              <button onClick={showPrev} className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/45 p-3 text-white" aria-label="Previous image"><ChevronLeft className="h-8 w-8" /></button>
              <button onClick={showNext} className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/45 p-3 text-white" aria-label="Next image"><ChevronRight className="h-8 w-8" /></button>
              <motion.img
                key={images[currentIndex].src}
                src={assetUrl(images[currentIndex].src)}
                alt={images[currentIndex].caption}
                onError={(e) => { e.currentTarget.src = "/image-fallback.svg"; }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="mx-auto max-h-[82vh] w-full rounded-3xl bg-white object-contain shadow-2xl"
              />
              <p className="mt-4 text-center text-lg font-bold text-white">{images[currentIndex].caption}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
